import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Modal from "../components/Modal";
import client from "../api/client";
import { useToast } from "../contexts/ToastContext";
import DifficultyBadge from "../components/DifficultyBadge";

interface HintPublic {
  id: string;
  level: number;
  content: string;
  cost: number;
  unlocked: boolean;
}

interface ChallengePublic {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: "EASY" | "MEDIUM" | "HARD" | "INSANE";
  basePoints: number;
  currentPoints: number;
  tags: string[];
  hasDockerInstance: boolean;
  isActive: boolean;
  solveCount: number;
  firstBloodUser: any;
  firstBloodAt: string | null;
  prerequisiteId: string | null;
  prerequisiteMet: boolean;
  solved: boolean;
  hints: HintPublic[];
}

interface ChallengeModalProps {
  challenge: ChallengePublic;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (challenge: ChallengePublic) => void;
}

export function ChallengeModal({
  challenge,
  isOpen,
  onClose,
  onUpdate,
}: ChallengeModalProps) {
  const { toast } = useToast();
  const [flag, setFlag] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [launchingDocker, setLaunchingDocker] = useState(false);
  const [hints, setHints] = useState<HintPublic[]>(challenge.hints);
  const [unlockingHint, setUnlockingHint] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFlag("");
      setSubmitSuccess(false);
      setSubmitError(null);
      setHints(challenge.hints);
    }
  }, [isOpen, challenge]);

  const handleSubmitFlag = async () => {
    if (!flag.trim() || submitting) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await client.post("/challenges/submit", {
        challengeId: challenge.id,
        flag: flag.trim(),
      });

      const result = response.data?.data || response.data;

      if (result.success) {
        setSubmitSuccess(true);
        toast({
          title: "Correct flag!",
          description: `+${result.pointsAwarded} points earned`,
          variant: "success",
        });
        if (onUpdate) {
          onUpdate({
            ...challenge,
            solved: true,
            currentPoints: result.newTotal ?? challenge.currentPoints,
          });
        }
      } else {
        setSubmitError(result.message || "Incorrect flag");
        setFlag("");
      }
    } catch (error: any) {
      const message = error.response?.data?.error || error.response?.data?.message || "Failed to submit flag";
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLaunchDocker = async () => {
    setLaunchingDocker(true);
    try {
      const response = await client.post(`/challenges/${challenge.id}/docker`);
      toast({
        title: "Instance launched",
        description: response.data.message || "Docker instance is starting",
        variant: "success",
      });
    } catch (error: any) {
      toast({
        title: "Failed to launch instance",
        description: error.response?.data?.message || "Please try again",
        variant: "error",
      });
    } finally {
      setLaunchingDocker(false);
    }
  };

  const handleUnlockHint = async (hintId: string) => {
    setUnlockingHint(hintId);
    try {
      const response = await client.post(`/challenges/${challenge.id}/hint`, {
        hintId,
      });

      const result = response.data.data;
      const updatedHint = { content: result.hint, cost: result.costDeducted };
      setHints((prev) =>
        prev.map((h) => (h.id === hintId ? { ...h, ...updatedHint, unlocked: true } : h))
      );

      toast({
        title: "Hint unlocked",
        description: `-${result.costDeducted} points`,
        variant: "info",
      });
    } catch (error: any) {
      toast({
        title: "Failed to unlock hint",
        description: error.response?.data?.message || "Please try again",
        variant: "error",
      });
    } finally {
      setUnlockingHint(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmitFlag();
    }
  };

  const descriptionParagraphs = challenge.description.split("\n").filter(Boolean);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="w-full max-h-[80vh] overflow-hidden">
        {/* Mobile Layout */}
        <div className="lg:hidden overflow-y-auto max-h-[90vh] p-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-mono text-ds-text-muted uppercase tracking-wider">
                {challenge.category}
              </span>
              <span className="text-ds-text-muted">•</span>
              <DifficultyBadge difficulty={challenge.difficulty} />
            </div>
            <h2 className="text-2xl font-bold text-ds-text">{challenge.title}</h2>
          </div>

          {/* Points Card */}
          <div className="bg-ds-bg border border-ds-border rounded-lg p-4 mb-6">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-mono font-bold text-ds-accent">
                {challenge.currentPoints}
              </span>
              <span className="text-ds-text-muted">points</span>
              {challenge.basePoints !== challenge.currentPoints && (
                <span className="text-sm font-mono text-ds-text-muted line-through">
                  {challenge.basePoints}
                </span>
              )}
            </div>
            <div className="mt-2 text-sm text-ds-text-muted font-mono">
              {challenge.solveCount} solve{challenge.solveCount !== 1 ? "s" : ""}
            </div>
            {challenge.firstBloodUser && (
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="px-2 py-0.5 bg-red-500/10 text-red-400 rounded font-medium">
                  First Blood
                </span>
                <span className="text-ds-text-muted">
                  {challenge.firstBloodUser.username || "Anonymous"}
                </span>
              </div>
            )}
          </div>

          {/* Prerequisite Warning */}
          {challenge.prerequisiteId && !challenge.solved && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-yellow-400">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">Prerequisite required</span>
              </div>
              <p className="text-sm text-ds-text-muted mt-1">
                You must solve the prerequisite challenge first
              </p>
            </div>
          )}

          {/* Description */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-ds-text-muted uppercase tracking-wider mb-3">
              Description
            </h3>
            <div className="text-ds-text leading-relaxed space-y-3">
              {descriptionParagraphs.map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
          </div>

          {/* Tags */}
          {challenge.tags.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-ds-text-muted uppercase tracking-wider mb-3">
                Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {challenge.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-ds-bg text-ds-text-muted text-sm rounded-full border border-ds-border"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Docker Button */}
          {challenge.hasDockerInstance && (
            <div className="mb-6">
              <button
                onClick={handleLaunchDocker}
                disabled={launchingDocker}
                className="w-full px-4 py-3 bg-ds-accent/10 border border-ds-accent/30 text-ds-accent rounded-lg font-medium hover:bg-ds-accent/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {launchingDocker ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Launching Instance...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
                      />
                    </svg>
                    Launch Instance
                  </span>
                )}
              </button>
            </div>
          )}

          {/* Flag Submission */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-ds-text-muted uppercase tracking-wider mb-3">
              Submit Flag
            </h3>
            {challenge.solved || submitSuccess ? (
              <div className="bg-ds-success/10 border border-ds-success/30 rounded-lg p-4">
                <div className="flex items-center gap-2 text-ds-success">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">Already solved</span>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <motion.div className="flex-1" animate={submitError ? { x: [-10, 10, -10, 10, 0] } : {}} transition={{ duration: 0.4 }}>
                  <input
                    type="text"
                    placeholder="CTF{...}"
                    value={flag}
                    onChange={(e) => {
                      setFlag(e.target.value);
                      setSubmitError(null);
                    }}
                    onKeyDown={handleKeyDown}
                    disabled={submitting || (challenge.prerequisiteId != null && !challenge.prerequisiteMet)}
                    className="w-full px-4 py-3 bg-ds-bg border border-ds-border rounded-lg text-ds-text font-mono placeholder-ds-muted focus:outline-none focus:border-ds-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </motion.div>
                <motion.button
                  onClick={handleSubmitFlag}
                  disabled={submitting || !flag.trim() || (challenge.prerequisiteId != null && !challenge.prerequisiteMet)}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 bg-ds-accent text-white rounded-lg font-medium hover:bg-ds-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    "Submit"
                  )}
                </motion.button>
              </div>
            )}
            {submitError && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-red-400 mt-2"
              >
                {submitError}
              </motion.p>
            )}
          </div>

          {/* Hints */}
          {hints.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-ds-text-muted uppercase tracking-wider mb-3">
                Hints
              </h3>
              <div className="space-y-2">
                {hints.map((hint) => (
                  <div
                    key={hint.id}
                    className="bg-ds-bg border border-ds-border rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-ds-text-muted">
                          Hint {hint.level}
                        </span>
                      </div>
                      {!hint.unlocked && (
                        <button
                          onClick={() => handleUnlockHint(hint.id)}
                          disabled={unlockingHint === hint.id || challenge.prerequisiteId !== null}
                          className="text-xs px-3 py-1 bg-ds-surface border border-ds-border rounded text-ds-text-muted hover:text-ds-text hover:border-ds-accent/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {unlockingHint === hint.id ? (
                            "Unlocking..."
                          ) : (
                            `Unlock for ${hint.cost} pts`
                          )}
                        </button>
                      )}
                    </div>
                    {hint.unlocked ? (
                      <p className="text-ds-text mt-2 text-sm">{hint.content}</p>
                    ) : (
                      <p className="text-ds-text-muted mt-2 text-sm italic">Locked</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:flex">
          {/* Left Column - Content */}
          <div className="flex-1 p-6 border-r border-ds-border overflow-y-auto max-h-[90vh]">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-mono text-ds-text-muted uppercase tracking-wider">
                  {challenge.category}
                </span>
                <span className="text-ds-text-muted">•</span>
                <DifficultyBadge difficulty={challenge.difficulty} />
              </div>
              <h2 className="text-2xl font-bold text-ds-text">{challenge.title}</h2>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-ds-text-muted uppercase tracking-wider mb-3">
                Description
              </h3>
              <div className="text-ds-text leading-relaxed space-y-3">
                {descriptionParagraphs.map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
            </div>

            {/* Tags */}
            {challenge.tags.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-ds-text-muted uppercase tracking-wider mb-3">
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {challenge.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-ds-bg text-ds-text-muted text-sm rounded-full border border-ds-border"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Docker Button */}
            {challenge.hasDockerInstance && (
              <div className="mb-6">
                <button
                  onClick={handleLaunchDocker}
                  disabled={launchingDocker}
                  className="w-full px-4 py-3 bg-ds-accent/10 border border-ds-accent/30 text-ds-accent rounded-lg font-medium hover:bg-ds-accent/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {launchingDocker ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Launching Instance...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
                        />
                      </svg>
                      Launch Instance
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* Flag Submission */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-ds-text-muted uppercase tracking-wider mb-3">
                Submit Flag
              </h3>
              {challenge.solved || submitSuccess ? (
                <div className="bg-ds-success/10 border border-ds-success/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-ds-success">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="font-medium">Already solved</span>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <motion.div className="flex-1" animate={submitError ? { x: [-10, 10, -10, 10, 0] } : {}} transition={{ duration: 0.4 }}>
                    <input
                      type="text"
                      placeholder="CTF{...}"
                      value={flag}
                      onChange={(e) => {
                        setFlag(e.target.value);
                        setSubmitError(null);
                      }}
                      onKeyDown={handleKeyDown}
                      disabled={submitting || (challenge.prerequisiteId != null && !challenge.prerequisiteMet)}
                      className="w-full px-4 py-3 bg-ds-bg border border-ds-border rounded-lg text-ds-text font-mono placeholder-ds-muted focus:outline-none focus:border-ds-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </motion.div>
                  <motion.button
                    onClick={handleSubmitFlag}
                    disabled={submitting || !flag.trim() || (challenge.prerequisiteId != null && !challenge.prerequisiteMet)}
                    whileTap={{ scale: 0.95 }}
                    className="px-6 py-3 bg-ds-accent text-white rounded-lg font-medium hover:bg-ds-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      "Submit"
                    )}
                  </motion.button>
                </div>
              )}
              {submitError && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-red-400 mt-2"
                >
                  {submitError}
                </motion.p>
              )}
            </div>

            {/* Hints */}
            {hints.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-ds-text-muted uppercase tracking-wider mb-3">
                  Hints
                </h3>
                <div className="space-y-2">
                  {hints.map((hint) => (
                    <div
                      key={hint.id}
                      className="bg-ds-bg border border-ds-border rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-ds-text-muted">
                            Hint {hint.level}
                          </span>
                        </div>
                        {!hint.unlocked && (
                          <button
                            onClick={() => handleUnlockHint(hint.id)}
                            disabled={unlockingHint === hint.id || challenge.prerequisiteId !== null}
                            className="text-xs px-3 py-1 bg-ds-surface border border-ds-border rounded text-ds-text-muted hover:text-ds-text hover:border-ds-accent/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {unlockingHint === hint.id ? (
                              "Unlocking..."
                            ) : (
                              `Unlock for ${hint.cost} pts`
                            )}
                          </button>
                        )}
                      </div>
                      {hint.unlocked ? (
                        <p className="text-ds-text mt-2 text-sm">{hint.content}</p>
                      ) : (
                        <p className="text-ds-text-muted mt-2 text-sm italic">Locked</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="w-72 p-6 bg-ds-bg">
            {/* Points Display */}
            <div className="mb-6">
              <div className="text-sm font-semibold text-ds-text-muted uppercase tracking-wider mb-2">
                Points
              </div>
              <div className="flex items-baseline gap-2">
                <motion.span
                  key={challenge.currentPoints}
                  initial={{ scale: 1.2, color: "var(--ds-accent)" }}
                  animate={{ scale: 1 }}
                  className="text-4xl font-mono font-bold text-ds-accent"
                >
                  {challenge.currentPoints}
                </motion.span>
                <span className="text-sm text-ds-text-muted">pts</span>
              </div>
              {challenge.basePoints !== challenge.currentPoints && (
                <div className="text-sm font-mono text-ds-text-muted line-through mt-1">
                  {challenge.basePoints} pts (base)
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="h-px bg-ds-border mb-6" />

            {/* Solve Count */}
            <div className="mb-6">
              <div className="text-sm font-semibold text-ds-text-muted uppercase tracking-wider mb-2">
                Solves
              </div>
              <div className="text-2xl font-mono font-bold text-ds-text">
                {challenge.solveCount}
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-ds-border mb-6" />

            {/* First Blood */}
            <div className="mb-6">
              <div className="text-sm font-semibold text-ds-text-muted uppercase tracking-wider mb-2">
                First Blood
              </div>
              {challenge.firstBloodUser ? (
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-red-500/10 text-red-400 rounded text-xs font-medium">
                    🩸
                  </span>
                  <div>
                    <div className="text-ds-text font-medium">
                      {challenge.firstBloodUser.username || "Anonymous"}
                    </div>
                    {challenge.firstBloodAt && (
                      <div className="text-xs text-ds-text-muted font-mono">
                        {new Date(challenge.firstBloodAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-ds-text-muted text-sm italic">No first blood yet</div>
              )}
            </div>

            {/* Divider */}
            <div className="h-px bg-ds-border mb-6" />

            {/* Prerequisite Warning */}
            {challenge.prerequisiteId && !challenge.solved && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 text-yellow-400">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium text-sm">Prerequisite required</span>
                </div>
                <p className="text-xs text-ds-text-muted mt-2">
                  Solve the prerequisite challenge to unlock this challenge
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}